import { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Body1, Divider, Tab, TabList } from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import { DocumentContext } from '@modules/documents';
import DocumentControlsLock from '@modules/documents/components/DocumentControlsLock';
import { getChangeTemplatePath } from '../paths';
import { EChangeTemplateSection } from '../types';

type TProps = {
  changeTemplateId: string;
  section: EChangeTemplateSection;
  isEditMode: boolean;
  assessmentId?: string;
  templateVersion?: string;
};

const ChangeTemplateSectionTabs = ({
  changeTemplateId,
  section,
  isEditMode,
  assessmentId,
  templateVersion,
}: TProps) => {
  const navigate = useNavigate();
  // Missing while no document is shown.
  const documentContext = useContext(DocumentContext);
  const getSectionPath = (next: EChangeTemplateSection) =>
    getChangeTemplatePath(changeTemplateId, { section: next, templateVersion, autoEdit: isEditMode });

  // Saves the shown document first, like a version change, instead of asking to leave.
  const showSection = async (next: EChangeTemplateSection) => {
    if (documentContext?.isEdit && !(await documentContext.save())) return;
    navigate(getSectionPath(next));
  };

  return (
    <DocumentControlsLock className="flex items-center gap-3">
      <TabList
        size="medium"
        selectedValue={section}
        onTabSelect={(_, data) => {
          // From an open assessment, its own tab returns to the table.
          if (data.value !== section || assessmentId) showSection(data.value as EChangeTemplateSection);
        }}
      >
        <Tab value={EChangeTemplateSection.GENERAL}>General</Tab>
        <Tab value={EChangeTemplateSection.REQUEST}>Request</Tab>
        <Tab value={EChangeTemplateSection.ASSESSMENTS}>Assessments</Tab>
      </TabList>
      {assessmentId && (
        <>
          <Divider vertical className="!grow-0 h-6" />
          <Link
            to={getSectionPath(EChangeTemplateSection.ASSESSMENTS)}
            onClick={(event) => {
              if (!documentContext?.isEdit) return;
              event.preventDefault();
              showSection(EChangeTemplateSection.ASSESSMENTS);
            }}
            // Colored like the inactive tabs beside it.
            className="flex items-center gap-1 text-neutralFg-2 hover:text-neutralFg-1"
          >
            <ArrowLeftRegular className="text-base" />
            <Body1>All assessments</Body1>
          </Link>
        </>
      )}
    </DocumentControlsLock>
  );
};

export default ChangeTemplateSectionTabs;
